import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0DatePicker, F0Dialog } from '@factorialco/f0-react'
import { Form } from 'factorial-form'
import {
  BulkCreateWorkSchedulePlanningVersionsDocument,
  EmployeesEmployeeSelectorInput,
  WorkScheduleEmployeesDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { dateToString, dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type WorkSchedule from 'modules/work_schedules/store/models/Schedule'

interface AddEmployeesModalProps {
  workSchedule: WorkSchedule
  onClose: () => void
  onSuccess: () => void
}

const AddEmployeesModal = ({ workSchedule, onClose, onSuccess }: AddEmployeesModalProps) => {
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeesEmployeeSelectorInput>({
    type: 'manual',
    ids: [],
  })

  // get employees already assigned to this work schedule

  const form = useLazyRef<Form>(
    () =>
      new Form(
        {
          effective_at: dateToString(moment(), 'backend'),
        },
        {
          effective_at: 'date',
        }
      )
  ).current

  const [bulkCreateWorkSchedulePlanningVersions, { loading: _loading }] = useMutation(
    BulkCreateWorkSchedulePlanningVersionsDocument,
    {
      refetchQueries: [WorkScheduleEmployeesDocument],
      extractorFn: (data) => data.timePlanningMutations.bulkCreateTimeplanningPlanningVersion,
    }
  )

  const onSubmit = async () => {
    const response = await bulkCreateWorkSchedulePlanningVersions({
      variables: {
        effectiveAt: dateUIToBackend(form.get('effective_at').value),
        scheduleId: workSchedule.attributes.id,
        employeeSelection: selectedEmployees,
      },
    })
    const handled = handleResult(response, () => undefined)
    if (handled.success) {
      onSuccess()
      onClose()
    }
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={() => {
        if (_loading) return
        onClose()
      }}
      title={i18n.t('settings.work_schedule.employees_tab.employee_modal.assign')}
      description={i18n.t('settings.work_schedule.employees_tab.employee_modal.description')}
      primaryAction={{
        label: i18n.t('settings.work_schedule.employees_tab.employee_modal.assign'),
        onClick: onSubmit,
        disabled: _loading,
        loading: _loading,
      }}
      secondaryAction={{
        label: i18n.t('settings.work_schedule.cancel'),
        onClick: onClose,
      }}
    >
      <>
        <EmployeeSelectorV2
          messages={{
            placeholder: i18n.t('common.select_users'),
          }}
          forcedFilters={{
            excludeIds: workSchedule.employeeIds,
          }}
          filters={['team']}
          onChange={setSelectedEmployees}
          multiple
          options={{
            hideSearchIcon: true,
          }}
        />

        <Box paddingY="s16">
          <F0DatePicker
            label={i18n.t('settings.work_schedule.employees_tab.table.header.date')}
            onChange={(date) => {
              if (!date?.value?.from) return
              form.get('effective_at').set(date.value.from)
            }}
            size="md"
            value={{
              granularity: 'day',
              value: {
                from: new Date(form.get('effective_at').value),
                to: new Date(form.get('effective_at').value),
              },
            }}
            hint={i18n.t('settings.work_schedule.employees_tab.employee_modal.footer_description')}
          />
        </Box>
      </>
    </F0Dialog>
  )
}

export default AddEmployeesModal
